import type { ReactElement } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { Icon, type IconName } from '@/components/atoms/icon';
import { Logo } from '@/components/atoms/logo';
import { useProfile } from '@/lib/queries/profile';
import { useIsAdmin } from '@/lib/hooks/use-is-admin';
import { cn } from '@/lib/cn';

type NavItem = {
  readonly key: string;
  readonly to?: '/' | '/library' | '/practice' | '/repertoire' | '/history' | '/settings' | '/admin';
  readonly label: string;
  readonly icon: IconName;
  readonly disabled?: boolean;
  readonly matchPrefix?: readonly string[];
  readonly adminOnly?: boolean;
};

const NAV_ITEMS: readonly NavItem[] = [
  { key: 'today',      to: '/',            label: 'Today',      icon: 'home' },
  { key: 'library',    to: '/library',     label: 'Library',    icon: 'book_open', matchPrefix: ['/library'] },
  { key: 'tunebooks',  label: 'Tunebooks',  icon: 'book',       disabled: true },
  { key: 'repertoire', to: '/repertoire',  label: 'Repertoire', icon: 'music',     matchPrefix: ['/repertoire'] },
  { key: 'practice',   to: '/practice',    label: 'Practice',   icon: 'play',      matchPrefix: ['/practice'] },
  { key: 'history',    to: '/history',     label: 'History',    icon: 'chart',     matchPrefix: ['/history', '/insights'] },
  { key: 'settings',   to: '/settings',    label: 'Settings',   icon: 'cog',       matchPrefix: ['/settings', '/profile'] },
  { key: 'admin',      to: '/admin',       label: 'Admin',      icon: 'shield',    matchPrefix: ['/admin'], adminOnly: true },
] as const;

function isNavActive(pathname: string, item: NavItem): boolean {
  if (item.to === '/') return pathname === '/';
  if (!item.to) return false;
  const prefixes = item.matchPrefix ?? [item.to];
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
}

export function DesktopSideNav(): ReactElement {
  const location = useLocation();
  const { data: profile } = useProfile();
  const isAdmin = useIsAdmin();
  const displayName = profile?.name ?? 'Your profile';
  const subline = profile?.email ?? 'Edit profile';
  const visibleItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  return (
    <aside
      className="hidden lg:flex flex-col w-[220px] shrink-0 h-full bg-[var(--bg-surface)] border-r border-[var(--rule)]"
    >
      <div className="h-16 px-5 flex items-center border-b border-[var(--rule)]">
        <Logo />
      </div>
      <nav aria-label="Primary" className="p-3 flex-1">
        {visibleItems.map((item) => {
          const isActive = isNavActive(location.pathname, item);
          const className = cn(
            'w-full h-10 px-3 rounded-[var(--r-sm)] flex items-center gap-3 text-[13px] font-semibold transition-colors mb-0.5',
            isActive
              ? 'bg-[var(--bg-soft)] text-[var(--ink)]'
              : 'text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-soft)]',
            item.disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent hover:text-[var(--ink-3)]',
          );
          if (item.disabled || !item.to) {
            return (
              <div
                key={item.key}
                aria-disabled="true"
                title="Coming soon"
                className={className}
              >
                <Icon name={item.icon} size={15} />
                {item.label}
              </div>
            );
          }
          return (
            <Link key={item.key} to={item.to} className={className}>
              <Icon name={item.icon} size={15} />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <Link
        to="/settings"
        className="block p-3 border-t border-[var(--rule)]"
      >
        <div className="flex items-center gap-2.5 p-2 rounded-[var(--r-sm)] hover:bg-[var(--bg-soft)]">
          <div className="w-8 h-8 rounded-full bg-[var(--brand)] flex items-center justify-center text-white text-[12px] font-semibold">
            {initials(displayName)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold text-[var(--ink)] truncate">{displayName}</div>
            <div className="text-[10px] text-[var(--ink-3)] truncate">{subline}</div>
          </div>
        </div>
      </Link>
    </aside>
  );
}
